import { CustomProvider, AppCheckToken } from 'firebase/app-check';

import { firebaseConfig } from '../firebaseConfig';

/**
 * A Custom App Check Provider that uses a Cloudflare Turnstile token
 * to fetch a valid Firebase App Check token from the backend.
 */
/**
 * Zustand des Turnstile-Widgets. Es beginnt beim Laden der Seite selbst mit der Prüfung ("solving").
 * Ein Reset bricht eine laufende Prüfung ab; auf langsamen Verbindungen würde sie sonst nie fertig.
 */
type ChallengeState = 'solving' | 'ready' | 'idle' | 'error';

/** Wie lange eine Token-Anfrage auf Turnstile wartet, bevor Firebase ohne App Check weitermacht. */
const TURNSTILE_WAIT_MS = 4000;

export class TurnstileAppCheckProvider extends CustomProvider {
  private turnstileToken: string | null = null;
  private challengeState: ChallengeState = 'solving';
  private tokenResolveQueue: ((token: string) => void)[] = [];
  private activeExchangePromise: Promise<AppCheckToken> | null = null;
  private resetCallback: (() => void) | null = null;

  constructor() {
    super({
      getToken: (): Promise<AppCheckToken> => {
        // Prevent concurrent duplicate requests causing Cloudflare "timeout-or-duplicate" errors.
        if (this.activeExchangePromise) {
          console.debug("[App Check] Returning active exchange promise to prevent duplicate verification.");
          return this.activeExchangePromise;
        }

        this.activeExchangePromise = (async (): Promise<AppCheckToken> => {
          try {
            // Neue Prüfung nur anstossen, wenn keine läuft (Token verbraucht, abgelaufen oder Fehler)
            if (!this.turnstileToken && this.challengeState !== 'solving' && this.resetCallback) {
              console.debug("[App Check] Requesting new Turnstile challenge...");
              this.challengeState = 'solving';
              this.resetCallback();
            }

            // 1. Wait for Turnstile widget to solve and provide a token
            const currentTurnstileToken = await this.getTurnstileToken();
            if (!currentTurnstileToken) {
              // Die Prüfung läuft weiter; die nächste Anfrage nutzt ihr Ergebnis. Kein Aufruf mit
              // Platzhalter-Token: Das Backend würde ihn ohnehin ablehnen (403).
              throw new Error(`Turnstile not solved within ${TURNSTILE_WAIT_MS / 1000}s`);
            }

            // 2. Call our Cloud Function verifyTurnstileToken using native fetch
            // We MUST use fetch instead of httpsCallable to prevent circular AppCheck dependency deadlocks!
            const projectId = firebaseConfig.projectId;
            const region = 'europe-west6';
            const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
            const useLoadBalancer = import.meta.env.VITE_USE_LOAD_BALANCER !== 'false';
            const apiDomain = import.meta.env.VITE_API_DOMAIN || 'https://api.caf-suite.ch';

            let functionUrl = '';
            if (isLocalhost) {
              functionUrl = `http://127.0.0.1:5001/${projectId}/${region}/verifyTurnstileToken`;
            } else if (useLoadBalancer) {
              functionUrl = `${apiDomain}/verifyturnstiletoken`;
            } else {
              // This routes through Firebase Hosting rewrites (`firebase.json`) natively.
              functionUrl = `/api/verifyTurnstileToken`;
            }

            console.debug("[App Check] Requesting Custom Token via raw fetch: " + functionUrl);

            const response = await fetch(functionUrl, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                data: {
                  turnstileToken: currentTurnstileToken,
                  appId: firebaseConfig.appId,
                }
              })
            });

            if (!response.ok) {
              throw new Error(`HTTP Error ${response.status} calling verifyTurnstileToken`);
            }

            const responseData = await response.json();
            // Firebase onCall returns the payload inside 'result'
            const result = responseData.result || responseData.data;

            if (!result || !result.token || !result.expireTimeMillis) {
              throw new Error("Invalid response from Cloud Function. Missing token or expiration.");
            }

            const { token, expireTimeMillis } = result;

            console.debug("[App Check] Turnstile Provider successfully issued Firebase App Check Token.");

            // Clear it so it's not reused (which would cause timeout-or-duplicate).
            this.turnstileToken = null;
            this.challengeState = 'idle';

            return {
              token,
              expireTimeMillis,
            };
          } catch (error) {
            console.error("TurnstileAppCheckProvider failed to get token:", error);
            // The Firebase SDK continues with a dummy token, which the backend rejects;
            // the next request calls this provider again.
            throw error;
          } finally {
            this.activeExchangePromise = null;
          }
        })();

        return this.activeExchangePromise;
      }
    });
  }

  /**
   * Register a callback to reset the Turnstile React Widget when a new challenge is needed.
   */
  public setResetCallback(cb: () => void) {
    this.resetCallback = cb;
  }

  /**
   * Sets the turnstile token obtained from the frontend widget.
   * Resolves any pending getToken() calls waiting for the Turnstile response.
   */
  public setTurnstileToken(token: string) {
    this.turnstileToken = token;
    this.challengeState = 'ready';
    // Resolve all waiting promises
    while (this.tokenResolveQueue.length > 0) {
      const resolve = this.tokenResolveQueue.shift();
      if (resolve) resolve(token);
    }
  }

  /**
   * Clears the turnstile token when it expires.
   */
  public clearTurnstileToken() {
    this.turnstileToken = null;
    this.challengeState = 'idle';
  }

  /**
   * Whether the Turnstile script has loaded at all. If it failed (e.g. offline at startup), retrying
   * requests cannot help; only a page reload fetches it again.
   */
  public isScriptLoaded(): boolean {
    return typeof window !== 'undefined' && 'turnstile' in window;
  }

  /**
   * Turnstile reported an error: the next token request starts a new challenge.
   */
  public markTurnstileError() {
    this.turnstileToken = null;
    this.challengeState = 'error';
  }

  /**
   * Internal helper to retrieve the cached Turnstile token 
   * or wait until the UI sets it via setTurnstileToken().
   */
  private getTurnstileToken(): Promise<string | null> {
    if (this.turnstileToken) {
      return Promise.resolve(this.turnstileToken);
    }

    return new Promise((resolve) => {
      const waiter = (token: string) => resolve(token);
      this.tokenResolveQueue.push(waiter);

      // A slow connection or Safari/iOS fingerprinting protection (the invisible widget then hangs
      // instead of failing) must not freeze Firebase Auth for 60 seconds. After the timeout we resolve
      // null; the challenge keeps running and a later request (e.g. AuthContext's retry) uses its token.
      setTimeout(() => {
        const index = this.tokenResolveQueue.indexOf(waiter);
        if (index > -1) {
          this.tokenResolveQueue.splice(index, 1);
          console.warn(`[App Check] Turnstile not solved within ${TURNSTILE_WAIT_MS / 1000}s (slow connection or Safari ITP). Continuing without App Check; the next request retries.`);
          resolve(null);
        }
      }, TURNSTILE_WAIT_MS);
    });
  }
}

// Global Singleton Instance to share between UI and firebaseService.ts
export const turnstileAppCheckProviderInstance = new TurnstileAppCheckProvider();
